import { Button, Col, Collapse, Divider, Row, Tabs, Typography } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../../context/app/context";
import GeneralSettingContext from "../../../context/general-setting/context";
import GeneralSettingContextProvider from "../../../context/general-setting/provider";
import { IUpdateGeneralSetting } from "../../../models/general-setting/request";
import {
    DEFAULT_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
    languages,
    MAX_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
    MIN_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
} from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import { getTranslations } from "../../../utils/helpers/translations";
import FormItem from "../../general/form-item";
import Controller from "../../form-components/controller";
import { MediumFor } from "../../../models/medium/enum";
import EndPoints from "../../../services/end-points";
import { IDiscountCode } from "../../../models/discount-code/response";
import RichEditor from "../../form-components/rich-text-editor";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { execute } from "../../../utils/helpers/execute";
import styles from "./style.module.scss";
import { useNavigate } from "react-router-dom";
import { IMedium } from "../../../models/medium/response";
import ImageFieldBlock from "../../general/image-field-block";
import { errorNotification, successNotification } from "../../../utils/helpers/notification";
import { appendImageProcessingFields, getImageProcessingMode, hasPendingImageUpload, resolveUploadFile } from "../../../utils/helpers/media-upload";
import {
    DollarOutlined,
    CarOutlined,
    MobileOutlined,
    GiftOutlined,
    TeamOutlined,
    PlusOutlined,
    DeleteOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { IShoppingDaypart } from "../../../models/general-setting/response";
import {
    DEFAULT_SHOPPING_DAYPARTS,
    getClockDistance,
    getShoppingDaypartDuration,
    getShoppingDaypartMidpoint,
    getShoppingDaypartTimelinePieces,
    getShoppingDaypartsValue,
    minutesToTime,
    SHOPPING_DAYPART_KEY_PATTERN,
    sortShoppingDaypartsByStart,
    timeToMinutes,
    validateShoppingDayparts,
} from "../../../utils/helpers/shopping-dayparts";

const Form = () => {
    const { t } = useTranslation();
    const [discountCode, setDiscountCode] = useState<IDiscountCode[]>([]);
    const [discountCodeLoading, setDiscountCodeLoading] = useState(true);
    const { direction } = useContext(AppContext);
    const { list, actions, loading } = useContext(GeneralSettingContext);
    const navigate = useNavigate();
    const [hasMore, setHasMore] = useState(true);
    const [query, setQuery] = useState({ page: 1, search: undefined });
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});

    const [newImages, setNewImages] = useState<Record<string, string | null>>({});
    const [newImageFiles, setNewImageFiles] = useState<Record<string, File | undefined>>({});
    const [originalImageFiles, setOriginalImageFiles] = useState<Record<string, File | undefined>>({});
    const [imageChanged, setImageChanged] = useState<Record<string, boolean>>({});
    const [withoutBackgrounds, setWithoutBackgrounds] = useState<Record<string, boolean>>({});
    const [preserveOriginals, setPreserveOriginals] = useState<Record<string, boolean>>({});

    const handleImageChange = (fieldName: string) => (response: IMedium | null) => {
        if (response) {
            setNewImages((prev) => ({ ...prev, [fieldName]: response.url }));
        } else {
            setNewImages((prev) => ({ ...prev, [fieldName]: null }));
        }
        setNewImageFiles((prev) => ({ ...prev, [fieldName]: response?.file }));
        setOriginalImageFiles((prev) => ({ ...prev, [fieldName]: response?.originalFile }));
    };

    const handleUploadNewImage = async (fieldName: string) => {
        const newImageUrl = newImages[fieldName];
        if (!newImageUrl) {
            errorNotification(t("no_new_image_to_upload"));
            return;
        }
        try {
            const preserveOriginal = preserveOriginals[fieldName] ?? false;
            const file = await resolveUploadFile({
                imageUrl: newImageUrl,
                processedFile: newImageFiles[fieldName],
                originalFile: originalImageFiles[fieldName],
                preserveOriginal,
                fallbackName: fieldName,
            });

            const formData = new FormData();
            formData.append("medium", file);
            formData.append("type", "1");
            formData.append("for", MediumFor.UserProfile);
            appendImageProcessingFields(
                formData,
                getImageProcessingMode(withoutBackgrounds[fieldName] ?? false, preserveOriginal),
            );

            const uploadResponse = await EndPoints.media.uploadMedia(formData);

            setValue(fieldName, uploadResponse.id, { shouldDirty: true });
            setImageChanged((prev) => ({ ...prev, [fieldName]: false }));
            setNewImages((prev) => ({ ...prev, [fieldName]: uploadResponse?.url ?? newImageUrl }));
            setNewImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
            setOriginalImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
            setPreserveOriginals((prev) => ({ ...prev, [fieldName]: false }));

            successNotification(t("image_uploaded_successfully"));
        } catch (error) {
            errorNotification(t("failed_to_upload_image"));
        }
    };

    const methods = useForm<any>({
        defaultValues: {
            sharingAppLink: list?.sharingAppLink ?? '',
            sharingAppMessage: getTranslations(list?.translations.sharingAppMessage),
            activatingPublicService: list?.activatingPublicService ?? false,
            activatePublicMessageClient: list?.activatePublicMessageClient ?? false,
            publicMessageClient: getTranslations(list?.translations.publicMessageClient),
            activatePublicMessagePharmacist: list?.activatePublicMessagePharmacist ?? false,
            publicMessagePharmacist: getTranslations(list?.translations.publicMessagePharmacist),
            activatePublicMessageDelivery: list?.activatePublicMessageDelivery ?? false,
            publicMessageDelivery: getTranslations(list?.translations?.publicMessageDelivery),
            maxNumberPharmaciesFindMedicine: list?.maxNumberPharmaciesFindMedicine,
            scoreValidity: list?.scoreValidity,
            chargingCustomerForReachStartCostOrder: true,
            kilometerCost: list?.kilometerCost,
            priceKilometerCustomer: list?.priceKilometerCustomer,
            currencySymbol: getTranslations(list?.translations?.currencySymbol),
            maxNumberPharmaciesFindPharmacie: list?.maxNumberPharmaciesFindPharmacie,
            pointMultiplicationFactor: list?.pointMultiplicationFactor,
            numberTimesIgnoredAllowed: list?.numberTimesIgnoredAllowed,
            showListPharmacies: list?.showListPharmacies,
            commission: list?.commission,
            deliveryWorkerAcceptanceTime: list?.deliveryWorkerAcceptanceTime,
            pharmacyAcceptanceTime: list?.pharmacyAcceptanceTime,
            timeBroadcast: list?.timeBroadcast,
            freeDelivery: list?.freeDelivery,
            freeDeliveryPharmacyPromotion: list?.freeDeliveryPharmacyPromotion,
            driverTimeBroadcast: list?.driverTimeBroadcast,
            maxDriversInOrder: list?.maxDriversInOrder,
            radiusSearchForDriver: list?.radiusSearchForDriver,
            homeHeaderClientEn: list?.homeHeaderClientEn?.id ?? null,
            homeHeaderClientAr: list?.homeHeaderClientAr?.id ?? null,
            homeHeaderDriverEn: list?.homeHeaderDriverEn?.id ?? null,
            homeHeaderDriverAr: list?.homeHeaderDriverAr?.id ?? null,
            homeHeaderInvestorEn: list?.homeHeaderInvestorEn?.id ?? null,
            homeHeaderInvestorAr: list?.homeHeaderInvestorAr?.id ?? null,
            homeHeaderClient: list?.homeHeaderClient?.id ?? null,
            homeHeaderDriver: list?.homeHeaderDriver?.id ?? null,
            homeHeaderInvestor: list?.homeHeaderInvestor?.id ?? null,
            facebook: list?.facebook,
            loyaltyProgramLink: list?.loyaltyProgramLink,
            delayedOrderSchedulingTime: list?.delayedOrderSchedulingTime,
            phone: list?.phone,
            telegram: list?.telegram,
            whatsApp: list?.whatsApp,
            instagram: list?.instagram,
            minimumLimit: list?.minimumLimit,
            upperLimit: list?.upperLimit,
            firstOrderFree: list?.firstOrderFree ?? false,
            clientAcceptanceTime: Number(list?.clientAcceptanceTime),
            pointPrice: list?.pointPrice,
            showMedicalSpecialties: list?.showMedicalSpecialties ?? false,
            showCompanies: list?.showCompanies ?? false,
            welcomeCouponActive: list?.welcomeCouponActive ?? false,
            welcomeCoupon: list?.welcomeCoupon?.id,
            welcomeMessageActivation: list?.welcomeMessageActivation ?? false,
            welcomeMessage: getTranslations(list?.translations?.welcomeMessage),
            maxNumOfUnResponseOrder: list?.maxNumOfUnResponseOrder,
            hotLine: list?.hotLine,
            fixedDeliveryPriceOnClient: list?.fixedDeliveryPriceOnClient,
            makeInvestorsNoResponseOffVisibility: list?.makeInvestorsNoResponseOffVisibility ?? false,
            ordersSummeryInvestorVisibility: list?.ordersSummeryInvestorVisibility ?? false,
            ordersSummeryGuestInvestorVisibility: list?.ordersSummeryGuestInvestorVisibility ?? false,
            startDateTimeOrdersSummeryInvestor: list?.startDateTimeOrdersSummeryInvestor ?? null,
            endDateTimeOrdersSummeryInvestor: list?.endDateTimeOrdersSummeryInvestor ?? null,
            startDateTimeOrdersGuestSummeryInvestor: list?.startDateTimeOrdersGuestSummeryInvestor ?? null,
            endDateTimeOrdersGuestSummeryInvestor: list?.endDateTimeOrdersGuestSummeryInvestor ?? null,
            circleRadiusMeters: list?.circleRadiusMeters ?? 0,
            exchangePrice: list?.exchangePrice ?? 0,
            priceTotalProductsToGetFreeDelivery: list?.priceTotalProductsToGetFreeDelivery ?? 0,
            enableInvitation: list?.enableInvitation ?? false,
            messageForSharingInvitation: getTranslations(list?.translations?.messageForSharingInvitation),
            smallMessageForInvitation: getTranslations(list?.translations?.smallMessageForInvitation),
            bigMessageForInvitation: getTranslations(list?.translations?.bigMessageForInvitation),
            titleInvitationSender: getTranslations(list?.translations?.titleInvitationSender),
            descriptionInvitationSender: getTranslations(list?.translations?.descriptionInvitationSender),
            imageIdSender: list?.imageIdSender?.id,
            imageIdForBigMessage: list?.imageIdForBigMessage?.id,
            imageIdForSmallMessage: list?.imageIdForSmallMessage?.id,
            titleInvitationReceiver: getTranslations(list?.translations?.titleInvitationReceiver),
            descriptionInvitationReceiver: getTranslations(list?.translations?.descriptionInvitationReceiver),
            imageIdReceiver: list?.imageIdReceiver?.id,
            verySmallMessageForInvitation: getTranslations(list?.translations?.verySmallMessageForInvitation) ?? null,
            imageIdForVerySmallMessage: list?.imageIdForVerySmallMessage?.id,
            dailyPaymentLimit: list?.dailyPaymentLimit ?? '',
            weeklyPaymentLimit: list?.weeklyPaymentLimit ?? '',
            monthlyPaymentLimit: list?.monthlyPaymentLimit ?? '',
            isEPaymentEnabled: list?.isEPaymentEnabled ?? false,
            isAlbarakaPaymentEnabled: list?.isAlbarakaPaymentEnabled ?? false,
            isAlshamPaymentEnable: list?.isAlshamPaymentEnable ?? false,
            expressDeliveryPrice: list?.expressDeliveryPrice ?? undefined,
            expressDeliveryService: list?.expressDeliveryService ?? false,
            fixedDeliveryTime: list?.fixedDeliveryTime ?? undefined,
            expressDeliveryTime: list?.expressDeliveryTime ?? undefined,
            orderStatisticsStartDate: list?.orderStatisticsStartDate ?? null,
            newProductPeriodDays: list?.newProductPeriodDays ?? undefined,
            useTripBasedDelivery: list?.useTripBasedDelivery ?? false,
            includeReturnTripInPayment: list?.includeReturnTripInPayment ?? false,
            routeStrategy: list?.routeStrategy ?? 1,
            orderCashCheckEnabled: list?.orderCashCheckEnabled ?? false,
            orderCashCheckScanPerPiece: list?.orderCashCheckScanPerPiece ?? false,
            weightedProductToleranceGrams:
                list?.weightedProductToleranceGrams ?? DEFAULT_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
            otpServiceUnavailable: list?.otpServiceUnavailable ?? false,
            enableRouteComparison: list?.enableRouteComparison ?? false,
            shoppingDayparts: getShoppingDaypartsValue(list?.shoppingDayparts),
        } as IUpdateGeneralSetting,
    });

    const { handleSubmit, reset, control, setValue, trigger } = methods;
    const shoppingDaypartsWatch = useWatch({
        control,
        name: "shoppingDayparts",
    }) as IShoppingDaypart[] | undefined;
    const shoppingDayparts = shoppingDaypartsWatch?.length
        ? sortShoppingDaypartsByStart(shoppingDaypartsWatch)
        : getShoppingDaypartsValue(list?.shoppingDayparts);
    const shoppingDaypartPieces = getShoppingDaypartTimelinePieces(shoppingDayparts);

    const updateShoppingDayparts = (nextDayparts: IShoppingDaypart[]) => {
        setValue("shoppingDayparts", sortShoppingDaypartsByStart(nextDayparts), {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
    };

    const generateShoppingDaypartKey = (dayparts: IShoppingDaypart[]) => {
        const keys = new Set(dayparts.map((daypart) => daypart.key));
        let index = dayparts.length + 1;

        while (keys.has(`daypart_${index}`)) {
            index += 1;
        }

        return `daypart_${index}`;
    };

    const formatDaypartDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        return `${hours.toString().padStart(2, "0")}:${mins
            .toString()
            .padStart(2, "0")}`;
    };

    const handleSplitShoppingDaypart = (index: number) => {
        const daypart = shoppingDayparts[index];
        if (!daypart) return;

        const duration = getShoppingDaypartDuration(daypart);
        if (duration <= 1) {
            errorNotification(t("shopping_daypart_too_short_to_split"));
            return;
        }

        const midpoint = getShoppingDaypartMidpoint(daypart);
        const nextDayparts = [...shoppingDayparts];

        nextDayparts[index] = {
            ...daypart,
            to: midpoint,
        };
        nextDayparts.splice(index + 1, 0, {
            key: generateShoppingDaypartKey(nextDayparts),
            nameAr: "فترة جديدة",
            nameEn: "New daypart",
            from: midpoint,
            to: daypart.to,
        });

        updateShoppingDayparts(nextDayparts);
    };

    const handleAddShoppingDaypart = () => {
        const longestIndex = shoppingDayparts.reduce(
            (selectedIndex, daypart, index) => {
                return getShoppingDaypartDuration(daypart) >
                    getShoppingDaypartDuration(shoppingDayparts[selectedIndex])
                    ? index
                    : selectedIndex;
            },
            0
        );

        handleSplitShoppingDaypart(longestIndex);
    };

    const handleMergeShoppingDaypartWithPrevious = (index: number) => {
        if (shoppingDayparts.length <= 1) return;

        const current = shoppingDayparts[index];
        const previousIndex =
            (index - 1 + shoppingDayparts.length) % shoppingDayparts.length;
        const nextDayparts = [...shoppingDayparts];

        nextDayparts[previousIndex] = {
            ...nextDayparts[previousIndex],
            to: current.to,
        };
        nextDayparts.splice(index, 1);

        updateShoppingDayparts(nextDayparts);
    };

    const handleMergeShoppingDaypartWithNext = (index: number) => {
        if (shoppingDayparts.length <= 1) return;

        const current = shoppingDayparts[index];
        const nextIndex = (index + 1) % shoppingDayparts.length;
        const nextDayparts = [...shoppingDayparts];

        nextDayparts[nextIndex] = {
            ...nextDayparts[nextIndex],
            from: current.from,
        };
        nextDayparts.splice(index, 1);

        updateShoppingDayparts(nextDayparts);
    };

    const handleShoppingDaypartBoundaryChange = (
        index: number,
        boundary: string
    ) => {
        if (timeToMinutes(boundary) === null || shoppingDayparts.length <= 1) {
            return;
        }

        const current = shoppingDayparts[index];
        const nextIndex = (index + 1) % shoppingDayparts.length;
        const next = shoppingDayparts[nextIndex];
        const offset = getClockDistance(current.from, boundary);
        const combinedDuration = getClockDistance(current.from, next.to);

        if (offset <= 0 || offset >= combinedDuration) {
            errorNotification(t("shopping_daypart_boundary_invalid"));
            return;
        }

        const nextDayparts = [...shoppingDayparts];
        nextDayparts[index] = {
            ...current,
            to: boundary,
        };
        nextDayparts[nextIndex] = {
            ...next,
            from: boundary,
        };

        updateShoppingDayparts(nextDayparts);
    };

    const handleImageDelete = async (fieldName: string) => {
        try {
            setImageLoading(prev => ({ ...prev, [fieldName]: true }));
            methods.setValue(fieldName, null);
            if (list) {
                list[fieldName] = null;
            }
        } finally {
            setImageLoading(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const handleRemoveNewImage = async (fieldName: string, restoreCurrentImage = true) => {
        const currentImage = restoreCurrentImage ? list?.[fieldName] : undefined;

        setValue(fieldName, currentImage?.id ?? undefined, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        await trigger(fieldName);
        setImageChanged((prev) => ({ ...prev, [fieldName]: false }));
        setNewImages((prev) => ({ ...prev, [fieldName]: null }));
        setNewImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
        setOriginalImageFiles((prev) => ({ ...prev, [fieldName]: undefined }));
        setPreserveOriginals((prev) => ({ ...prev, [fieldName]: false }));
        setWithoutBackgrounds((prev) => ({
            ...prev,
            [fieldName]: Boolean(currentImage?.without_background),
        }));
    };

    useEffect(() => {
        reset({
            sharingAppLink: list?.sharingAppLink ?? '',
            sharingAppMessage: getTranslations(list?.translations.sharingAppMessage),
            activatingPublicService: list?.activatingPublicService ?? false,
            activatePublicMessageClient: list?.activatePublicMessageClient ?? false,
            publicMessageClient: getTranslations(list?.translations.publicMessageClient),
            activatePublicMessagePharmacist: list?.activatePublicMessagePharmacist ?? false,
            publicMessagePharmacist: getTranslations(list?.translations.publicMessagePharmacist),
            activatePublicMessageDelivery: list?.activatePublicMessageDelivery ?? false,
            publicMessageDelivery: getTranslations(list?.translations?.publicMessageDelivery),
            maxNumberPharmaciesFindMedicine: list?.maxNumberPharmaciesFindMedicine,
            scoreValidity: list?.scoreValidity,
            chargingCustomerForReachStartCostOrder: true,
            kilometerCost: list?.kilometerCost,
            priceKilometerCustomer: list?.priceKilometerCustomer,
            currencySymbol: getTranslations(list?.translations?.currencySymbol),
            maxNumberPharmaciesFindPharmacie: list?.maxNumberPharmaciesFindPharmacie,
            pointMultiplicationFactor: list?.pointMultiplicationFactor,
            numberTimesIgnoredAllowed: list?.numberTimesIgnoredAllowed,
            showListPharmacies: list?.showListPharmacies,
            commission: list?.commission,
            deliveryWorkerAcceptanceTime: list?.deliveryWorkerAcceptanceTime,
            pharmacyAcceptanceTime: list?.pharmacyAcceptanceTime,
            timeBroadcast: list?.timeBroadcast,
            freeDelivery: list?.freeDelivery,
            priceTotalProductsToGetFreeDelivery: list?.priceTotalProductsToGetFreeDelivery,
            freeDeliveryPharmacyPromotion: list?.freeDeliveryPharmacyPromotion,
            driverTimeBroadcast: list?.driverTimeBroadcast,
            maxDriversInOrder: list?.maxDriversInOrder,
            radiusSearchForDriver: list?.radiusSearchForDriver,
            homeHeaderClientEn: list?.homeHeaderClientEn?.id ?? null,
            homeHeaderClientAr: list?.homeHeaderClientAr?.id ?? null,
            homeHeaderDriverEn: list?.homeHeaderDriverEn?.id ?? null,
            homeHeaderDriverAr: list?.homeHeaderDriverAr?.id ?? null,
            homeHeaderInvestorEn: list?.homeHeaderInvestorEn?.id ?? null,
            homeHeaderInvestorAr: list?.homeHeaderInvestorAr?.id ?? null,
            homeHeaderClient: list?.homeHeaderClient?.id ?? null,
            homeHeaderDriver: list?.homeHeaderDriver?.id ?? null,
            homeHeaderInvestor: list?.homeHeaderInvestor?.id ?? null,
            facebook: list?.facebook,
            loyaltyProgramLink: list?.loyaltyProgramLink,
            delayedOrderSchedulingTime: list?.delayedOrderSchedulingTime,
            phone: list?.phone,
            telegram: list?.telegram,
            whatsApp: list?.whatsApp,
            instagram: list?.instagram,
            minimumLimit: list?.minimumLimit,
            upperLimit: list?.upperLimit,
            firstOrderFree: list?.firstOrderFree ?? false,
            clientAcceptanceTime: Number(list?.clientAcceptanceTime),
            pointPrice: list?.pointPrice,
            showMedicalSpecialties: list?.showMedicalSpecialties ?? false,
            showCompanies: list?.showCompanies ?? false,
            welcomeCouponActive: list?.welcomeCouponActive ?? false,
            welcomeCoupon: list?.welcomeCoupon?.id,
            welcomeMessageActivation: list?.welcomeMessageActivation ?? false,
            welcomeMessage: getTranslations(list?.translations?.welcomeMessage),
            maxNumOfUnResponseOrder: list?.maxNumOfUnResponseOrder,
            hotLine: list?.hotLine,
            fixedDeliveryPriceOnClient: list?.fixedDeliveryPriceOnClient,
            makeInvestorsNoResponseOffVisibility: list?.makeInvestorsNoResponseOffVisibility ?? false,
            ordersSummeryInvestorVisibility: list?.ordersSummeryInvestorVisibility ?? false,
            ordersSummeryGuestInvestorVisibility: list?.ordersSummeryGuestInvestorVisibility ?? false,
            startDateTimeOrdersSummeryInvestor: list?.startDateTimeOrdersSummeryInvestor ?? null,
            endDateTimeOrdersSummeryInvestor: list?.endDateTimeOrdersSummeryInvestor ?? null,
            startDateTimeOrdersGuestSummeryInvestor: list?.startDateTimeOrdersGuestSummeryInvestor ?? null,
            endDateTimeOrdersGuestSummeryInvestor: list?.endDateTimeOrdersGuestSummeryInvestor ?? null,
            circleRadiusMeters: list?.circleRadiusMeters ?? 0,
            exchangePrice: list?.exchangePrice ?? 0,
            enableInvitation: list?.enableInvitation ?? false,
            messageForSharingInvitation: getTranslations(list?.translations?.messageForSharingInvitation),
            smallMessageForInvitation: getTranslations(list?.translations?.smallMessageForInvitation),
            bigMessageForInvitation: getTranslations(list?.translations?.bigMessageForInvitation),
            titleInvitationSender: getTranslations(list?.translations?.titleInvitationSender),
            descriptionInvitationSender: getTranslations(list?.translations?.descriptionInvitationSender),
            imageIdSender: list?.imageIdSender?.id,
            imageIdForBigMessage: list?.imageIdForBigMessage?.id,
            imageIdForSmallMessage: list?.imageIdForSmallMessage?.id,
            imageIdReceiver: list?.imageIdReceiver?.id,
            imageIdForVerySmallMessage: list?.imageIdForVerySmallMessage?.id,
            titleInvitationReceiver: getTranslations(list?.translations?.titleInvitationReceiver),
            descriptionInvitationReceiver: getTranslations(list?.translations?.descriptionInvitationReceiver),
            verySmallMessageForInvitation: getTranslations(list?.translations?.verySmallMessageForInvitation) ?? null,
            dailyPaymentLimit: list?.dailyPaymentLimit ?? '',
            weeklyPaymentLimit: list?.weeklyPaymentLimit ?? '',
            monthlyPaymentLimit: list?.monthlyPaymentLimit ?? '',
            isEPaymentEnabled: list?.isEPaymentEnabled ?? false,
            isAlbarakaPaymentEnabled: list?.isAlbarakaPaymentEnabled ?? false,
            isAlshamPaymentEnable: list?.isAlshamPaymentEnable ?? false,
            expressDeliveryPrice: list?.expressDeliveryPrice ?? undefined,
            expressDeliveryService: list?.expressDeliveryService ?? false,
            fixedDeliveryTime: list?.fixedDeliveryTime ?? undefined,
            expressDeliveryTime: list?.expressDeliveryTime ?? undefined,
            orderStatisticsStartDate: list?.orderStatisticsStartDate ?? null,
            newProductPeriodDays: list?.newProductPeriodDays ?? undefined,
            useTripBasedDelivery: list?.useTripBasedDelivery ?? false,
            includeReturnTripInPayment: list?.includeReturnTripInPayment ?? false,
            routeStrategy: list?.routeStrategy ?? 1,
            orderCashCheckEnabled: list?.orderCashCheckEnabled ?? false,
            orderCashCheckScanPerPiece: list?.orderCashCheckScanPerPiece ?? false,
            weightedProductToleranceGrams:
                list?.weightedProductToleranceGrams ?? DEFAULT_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
            otpServiceUnavailable: list?.otpServiceUnavailable ?? false,
            enableRouteComparison: list?.enableRouteComparison ?? false,
            shoppingDayparts: getShoppingDaypartsValue(list?.shoppingDayparts),
        } as IUpdateGeneralSetting);

        const newWithoutBackgrounds: Record<string, boolean> = {};
        fieldNames.forEach((field) => {
            newWithoutBackgrounds[field] = Boolean(list?.[field]?.without_background);
        });
        setWithoutBackgrounds(newWithoutBackgrounds);
    }, [list]);

    // FieldBuilder drops `min: 0` (falsy), so the range is validated by hand here.
    // Leaving the field empty is allowed: the backend keeps the stored value.
    const validateWeightedProductTolerance = (value: any) => {
        if (value === "" || value == null) return true;

        const grams = Number(value);

        if (!Number.isInteger(grams)) {
            return t("weighted_product_tolerance_grams_invalid");
        }

        if (
            grams < MIN_WEIGHTED_PRODUCT_TOLERANCE_GRAMS ||
            grams > MAX_WEIGHTED_PRODUCT_TOLERANCE_GRAMS
        ) {
            return t("weighted_product_tolerance_grams_range", {
                min: MIN_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
                max: MAX_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
            });
        }

        return true;
    };

    const onSubmit = async (data: any) => {
        if (hasPendingImageUpload(imageChanged, newImages)) {
            errorNotification(t("pending_image_upload_message"));
            return;
        }

        const shoppingDaypartsValidation = validateShoppingDayparts(
            data?.shoppingDayparts,
            {
                required: t("shopping_dayparts_required"),
                invalid: t("shopping_dayparts_invalid"),
                duplicate: t("shopping_dayparts_duplicate"),
                sameTime: t("shopping_dayparts_same_time"),
                coverage: t("shopping_dayparts_coverage"),
            }
        );

        if (shoppingDaypartsValidation !== true) {
            errorNotification(shoppingDaypartsValidation);
            return;
        }

        const shoppingDayparts = getShoppingDaypartsValue(
            data?.shoppingDayparts
        ).map((daypart) => ({
            ...daypart,
            key: daypart.key.trim(),
            nameAr: daypart.nameAr.trim(),
            nameEn: daypart.nameEn.trim(),
        }));

        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                await actions.updateGeneralSetting({
                    ...data,
                    shoppingDayparts,
                    clientAcceptanceTime: Number(data?.clientAcceptanceTime),
                    orderStatisticsStartDate: data?.orderStatisticsStartDate || null,
                    routeStrategy: data?.routeStrategy ? Number(data.routeStrategy) : 1,
                    // The number input hands back a string, and an empty field must go
                    // out as null so the backend keeps whatever it already has stored.
                    weightedProductToleranceGrams:
                        data?.weightedProductToleranceGrams === "" ||
                            data?.weightedProductToleranceGrams == null
                            ? null
                            : Number(data.weightedProductToleranceGrams),
                    welcomeCoupon: data?.welcomeCoupon || null,
                });
                navigate(-1);
            },
            fallback: (error) => { },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    useEffect(() => {
        const getGeneralSetting = async () => {
            await actions.getGeneralSetting();
        };
        getGeneralSetting();
    }, [direction]);

    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        if (scrollHeight - scrollTop === clientHeight && hasMore && !discountCodeLoading) {
            setQuery({ ...query, page: query.page + 1 });
        }
    };

    const handleSearch = (value) => {
        setQuery({ page: 1, search: value });
        setDiscountCode([]);
    };

    const getDiscountCode = async () => {
        try {
            const { data } = await EndPoints.discountCode.getAllDiscountCode({
                light: true,
                page: query.page,
                perPage: 200,
                keyword: query?.search == "" ? undefined : query?.search,
            });
            const newOptions = data?.data;
            setDiscountCode((prevOptions) => [...prevOptions, ...newOptions]);
            setHasMore(data.lastPage == query.page ? false : true);
            setDiscountCodeLoading(false);
        } catch (error) {
        } finally {
            setDiscountCodeLoading(false);
        }
    };

    useEffect(() => {
        getDiscountCode();
    }, [direction, query]);

    const fieldNames = [
        "homeHeaderClientEn",
        "homeHeaderClientAr",
        "homeHeaderDriverEn",
        "homeHeaderDriverAr",
        "homeHeaderInvestorEn",
        "homeHeaderInvestorAr",
        "homeHeaderClient",
        "homeHeaderDriver",
        "homeHeaderInvestor",
        "imageIdSender",
        "imageIdForBigMessage",
        "imageIdForSmallMessage",
        "imageIdReceiver",
        "imageIdForVerySmallMessage"
    ];

    const invitationImageFields = [
        "imageIdSender",
        "imageIdReceiver",
        "imageIdForBigMessage",
        "imageIdForSmallMessage",
        "imageIdForVerySmallMessage"
    ];

    const headerImageFields = [
        "homeHeaderClient",
        "homeHeaderClientEn",
        "homeHeaderClientAr",
        "homeHeaderDriver",
        "homeHeaderDriverEn",
        "homeHeaderDriverAr",
        "homeHeaderInvestor",
        "homeHeaderInvestorEn",
        "homeHeaderInvestorAr",
    ];

    // Watch all image fields at once
    const watchedFields = useWatch({ control, name: fieldNames });

    useEffect(() => {
        if (!list) return;

        const updatedImageChanged: Record<string, boolean> = {};
        fieldNames.forEach((field, index) => {
            const currentId = watchedFields?.[index];
            const originalId = list?.[field]?.id;
            updatedImageChanged[field] = Boolean(newImageFiles[field]) && currentId !== originalId;
        });
        setImageChanged(updatedImageChanged);
    }, [JSON.stringify(watchedFields), list, newImageFiles]);

    // ========== Tab 1: التسعير والدفع ==========
    const PricingPaymentTab = () => (
        <Collapse
            defaultActiveKey={['currency', 'ePayment', 'loyalty']}
            items={[
                {
                    key: 'currency',
                    label: t("currency_and_pricing"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={12} md={12} lg={12} key={`currency-${index}`}>
                                    <FieldBuilder
                                        name={`currencySymbol[${lang?.code}]`}
                                        label={`${t("currencySymbol")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="exchangePrice" label={t("exchange_price")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="commission" label={t("commission")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="minimumLimit" label={t("minimum_limit")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="upperLimit" label={t("upper_limit")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'ePayment',
                    label: t("e_payment_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("isEPaymentEnabled")} name="isEPaymentEnabled" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("isAlbarakaPaymentEnabled")} name="isAlbarakaPaymentEnabled" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("isAlshamPaymentEnable")} name="isAlshamPaymentEnable" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder name="dailyPaymentLimit" label={t("dailyPaymentLimit")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder name="weeklyPaymentLimit" label={t("weeklyPaymentLimit")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder name="monthlyPaymentLimit" label={t("monthlyPaymentLimit")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'loyalty',
                    label: t("loyalty_and_rewards"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="pointPrice" label={t("pointPrice")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="pointMultiplicationFactor" label={t("pointMultiplicationFactor")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="scoreValidity" label={t("scoreValidity")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="loyaltyProgramLink" label={t("loyaltyProgramLink")} input={{ type: "text" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 2: التوصيل والطلبات ==========
    const DeliveryOrdersTab = () => (
        <Collapse
            defaultActiveKey={['deliveryPricing', 'deliveryOptions', 'driver', 'pharmacy', 'orders', 'shoppingDayparts']}
            items={[
                {
                    key: 'deliveryPricing',
                    label: t("delivery_pricing"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="kilometerCost" label={t("kilometerCost")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="priceKilometerCustomer" label={t("priceKilometerCustomer")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="fixedDeliveryPriceOnClient" label={t("fixed_delivery_price_on_client")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="priceTotalProductsToGetFreeDelivery" label={t("priceTotalProductsToGetFreeDelivery")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="expressDeliveryPrice" label={t("express_delivery_price")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'deliveryOptions',
                    label: t("delivery_options"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("freeDelivery")} name="freeDelivery" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("freeDeliveryPharmacyPromotion")} name="freeDeliveryPharmacyPromotion" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("express_delivery_service")} name="expressDeliveryService" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("use_trip_based_delivery")} name="useTripBasedDelivery" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("include_return_trip_in_payment")} name="includeReturnTripInPayment" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("first_order_free")} name="firstOrderFree" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder
                                    name="routeStrategy"
                                    label={t("route_strategy")}
                                    input={{
                                        type: "select",
                                        options: [
                                            { label: t("strategy_optimized"), value: 1 },
                                            { label: t("strategy_hybrid_shortest"), value: 2 },
                                            { label: t("strategy_nearest_neighbor"), value: 3 },
                                        ],
                                    }}
                                    rules={{ required: false }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("enable_route_comparison")} name="enableRouteComparison" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="fixedDeliveryTime" label={t("fixed_delivery_time_minutes")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="expressDeliveryTime" label={t("express_delivery_time_minutes")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder
                                    name="orderStatisticsStartDate"
                                    label={t("order_statistics_start_date")}
                                    input={{ type: "date-picker", format: "YYYY-MM-DD", showTime: false }}
                                    rules={{ required: false }}
                                />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'driver',
                    label: t("driver_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="deliveryWorkerAcceptanceTime" label={t("deliveryWorkerAcceptanceTime")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="driverTimeBroadcast" label={t("driverTimeBroadcast")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxDriversInOrder" label={t("maxDriversInOrder")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="radiusSearchForDriver" label={t("radiusSearchForDriver")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="numberTimesIgnoredAllowed" label={t("numberTimesIgnoredAllowed")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'pharmacy',
                    label: t("pharmacy_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="pharmacyAcceptanceTime" label={t("pharmacyAcceptanceTime")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxNumberPharmaciesFindPharmacie" label={t("maxNumberPharmaciesFindPharmacie")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxNumberPharmaciesFindMedicine" label={t("maxNumberPharmaciesFindMedicine")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("showListPharmacies")} name="showListPharmacies" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'orders',
                    label: t("order_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="clientAcceptanceTime" label={t("client_acceptance_time")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="timeBroadcast" label={t("timeBroadcast")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="maxNumOfUnResponseOrder" label={t("maxNumOfUnResponseOrder")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="delayedOrderSchedulingTime" label={t("delayedOrderSchedulingTime")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder label={t("order_cash_check_enabled")} name="orderCashCheckEnabled" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder label={t("order_cash_check_scan_per_piece")} name="orderCashCheckScanPerPiece" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder
                                    name="weightedProductToleranceGrams"
                                    label={t("weighted_product_tolerance_grams")}
                                    input={{ type: "number" }}
                                    suffix={t("gram")}
                                    rules={{ validate: validateWeightedProductTolerance }}
                                />
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {t("weighted_product_tolerance_grams_hint")}
                                </Typography.Text>
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'shoppingDayparts',
                    label: t("shopping_dayparts"),
                    children: (
                        <Row gutter={[16, 14]}>
                            <Col span={24}>
                                <div className={styles.daypartEditorHeader}>
                                    <div>
                                        <div className={styles.daypartEditorTitle}>
                                            {t("shopping_dayparts_full_day")}
                                        </div>
                                        <div className={styles.daypartEditorHint}>
                                            {t("shopping_dayparts_editor_hint")}
                                        </div>
                                    </div>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => {
                                            updateShoppingDayparts(
                                                DEFAULT_SHOPPING_DAYPARTS.map((daypart) => ({ ...daypart }))
                                            );
                                        }}
                                    >
                                        {t("reset")}
                                    </Button>
                                </div>
                            </Col>
                            <Col span={24}>
                                <div className={styles.daypartTimeline}>
                                    <div className={styles.daypartTrack}>
                                        {shoppingDaypartPieces.map((piece, pieceIndex) => (
                                            <button
                                                type="button"
                                                key={`${piece.daypart.key}-${piece.start}-${piece.end}`}
                                                className={`${styles.daypartSegment} ${styles[`daypartTone${piece.sourceIndex % 6}`]}`}
                                                style={{
                                                    width: `${(piece.duration / (24 * 60)) * 100}%`,
                                                }}
                                                title={`${piece.daypart.nameAr} / ${piece.daypart.nameEn}: ${minutesToTime(piece.start)} - ${minutesToTime(piece.end)}`}
                                                onClick={() => handleSplitShoppingDaypart(piece.sourceIndex)}
                                            >
                                                {piece.duration >= 90 ? (
                                                    <>
                                                        <span>{piece.daypart.nameAr || piece.daypart.nameEn}</span>
                                                        <small>
                                                            {minutesToTime(piece.start)} - {minutesToTime(piece.end)}
                                                        </small>
                                                    </>
                                                ) : (
                                                    <span>{pieceIndex + 1}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                    <div className={styles.daypartTicks}>
                                        {["00:00", "04:00", "08:00", "12:00", "16:00", "20:00", "24:00"].map((tick) => (
                                            <span key={tick}>{tick}</span>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                            {shoppingDayparts.map((daypart, index) => {
                                const isSingleDaypart = shoppingDayparts.length === 1;
                                const duration = formatDaypartDuration(
                                    getShoppingDaypartDuration(daypart)
                                );

                                return (
                                    <Col span={24} key={`${daypart.key}-${index}`}>
                                        <div className={styles.daypartRow}>
                                            <div
                                                className={`${styles.daypartMarker} ${styles[`daypartTone${index % 6}`]}`}
                                            />
                                            <Row gutter={[12, 8]} align="middle" className={styles.daypartRowGrid}>
                                                <Col xs={24} sm={12} md={8} lg={6}>
                                                    <FieldBuilder
                                                        name={`shoppingDayparts.${index}.nameAr`}
                                                        label={t("name_ar")}
                                                        input={{ type: "text" }}
                                                        rules={{ required: true, maxLength: 100 }}
                                                    />
                                                </Col>
                                                <Col xs={24} sm={12} md={8} lg={6}>
                                                    <FieldBuilder
                                                        name={`shoppingDayparts.${index}.nameEn`}
                                                        label={t("name_en")}
                                                        input={{ type: "text" }}
                                                        rules={{ required: true, maxLength: 100 }}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6} md={4} lg={3}>
                                                    <div className={styles.daypartTimeBox}>
                                                        <span>{t("from")}</span>
                                                        <strong>{daypart.from}</strong>
                                                    </div>
                                                </Col>
                                                <Col xs={12} sm={6} md={4} lg={3}>
                                                    <label className={styles.daypartBoundary}>
                                                        <span>{t("to")}</span>
                                                        <input
                                                            type="time"
                                                            value={daypart.to}
                                                            onChange={(event) =>
                                                                handleShoppingDaypartBoundaryChange(
                                                                    index,
                                                                    event.target.value
                                                                )
                                                            }
                                                        />
                                                    </label>
                                                </Col>
                                                <Col xs={12} sm={6} md={4} lg={2}>
                                                    <div className={styles.daypartTimeBox}>
                                                        <span>{t("duration")}</span>
                                                        <strong>{duration}</strong>
                                                    </div>
                                                </Col>
                                                <Col xs={24} sm={18} md={12} lg={4}>
                                                    <div className={styles.daypartActions}>
                                                        <Button
                                                            size="small"
                                                            icon={<PlusOutlined />}
                                                            onClick={() => handleSplitShoppingDaypart(index)}
                                                        >
                                                            {t("split_period")}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            disabled={isSingleDaypart}
                                                            onClick={() =>
                                                                handleMergeShoppingDaypartWithPrevious(index)
                                                            }
                                                        >
                                                            {t("merge_with_previous")}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            disabled={isSingleDaypart}
                                                            onClick={() =>
                                                                handleMergeShoppingDaypartWithNext(index)
                                                            }
                                                        >
                                                            {t("merge_with_next")}
                                                        </Button>
                                                    </div>
                                                </Col>
                                                <Col span={24}>
                                                    <details className={styles.daypartAdvanced}>
                                                        <summary>{t("advanced_options")}</summary>
                                                        <Row gutter={[12, 8]}>
                                                            <Col xs={24} sm={12} md={8} lg={6}>
                                                                <FieldBuilder
                                                                    name={`shoppingDayparts.${index}.key`}
                                                                    label={t("key")}
                                                                    input={{ type: "text" }}
                                                                    rules={{
                                                                        required: true,
                                                                        maxLength: 50,
                                                                        pattern: {
                                                                            value: SHOPPING_DAYPART_KEY_PATTERN,
                                                                            message: t("shopping_daypart_key_pattern"),
                                                                        },
                                                                    }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </details>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                );
                            })}
                            <Col span={24}>
                                <div className={styles.daypartActions}>
                                    <Button
                                        icon={<PlusOutlined />}
                                        onClick={handleAddShoppingDaypart}
                                    >
                                        {t("add")}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 3: التطبيق والتواصل ==========
    const AppCommunicationTab = () => (
        <Collapse
            defaultActiveKey={['publicMessages', 'welcome', 'sharing', 'social', 'general']}
            items={[
                {
                    key: 'publicMessages',
                    label: t("public_messages"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgClient-${index}`}>
                                    <FieldBuilder
                                        name={`publicMessageClient[${lang?.code}]`}
                                        label={`${t("publicMessageClient")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgPharmacist-${index}`}>
                                    <FieldBuilder
                                        name={`publicMessagePharmacist[${lang?.code}]`}
                                        label={`${t("publicMessagePharmacist")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgDelivery-${index}`}>
                                    <FieldBuilder
                                        name={`publicMessageDelivery[${lang?.code}]`}
                                        label={`${t("publicMessageDlivery")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                            ))}
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("activatePublicMessageClient")} name="activatePublicMessageClient" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("activatePublicMessagePharmacist")} name="activatePublicMessagePharmacist" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("activatePublicMessageDelivery")} name="activatePublicMessageDelivery" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'welcome',
                    label: t("welcome_settings"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`welcome-${index}`}>
                                    <FieldBuilder
                                        name={`welcomeMessage[${lang?.code}]`}
                                        label={`${t("welcome_message")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("welcomeMessageActivation")} name="welcomeMessageActivation" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder
                                    name="welcomeCoupon"
                                    label={t("welcomeCoupon")}
                                    width="large"
                                    input={{
                                        type: "select-pagination",
                                        handleScroll: handleScroll,
                                        handleSearch: handleSearch,
                                        loading: discountCodeLoading,
                                        allowSearch: true,
                                        options: discountCode?.map((code, index) => ({
                                            key: index,
                                            label: code.name,
                                            value: code.id,
                                        })),
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("welcomeCouponActive")} name="welcomeCouponActive" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'sharing',
                    label: t("app_sharing"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <FieldBuilder name="sharingAppLink" label={t("sharingAppLink")} input={{ type: "text" }} rules={{ required: true }} />
                            </Col>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={12} md={12} lg={12} key={`sharing-${index}`}>
                                    <FieldBuilder
                                        name={`sharingAppMessage[${lang?.code}]`}
                                        label={`${t("sharingAppMessage")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'social',
                    label: t("social_contact"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="facebook" label={t("facebook")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="instagram" label={t("instagram")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="telegram" label={t("telegram")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="whatsApp" label={t("whatsApp")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="phone" label={t("phone")} input={{ type: "text" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="hotLine" label={t("hotLine")} input={{ type: "text" }} rules={{ required: false, minLength: 4, maxLength: 7 }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'general',
                    label: t("general_options"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("activatingPublicService")} name="activatingPublicService" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("showMedicalSpecialties")} name="showMedicalSpecialties" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("showCompanies")} name="showCompanies" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={6} lg={6}>
                                <FieldBuilder label={t("otp_service_unavailable")} name="otpServiceUnavailable" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="circleRadiusMeters" label={t("circleRadiusMeters")} input={{ type: "number" }} rules={{ required: true }} />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <FieldBuilder name="newProductPeriodDays" label={t("new_product_period_days")} input={{ type: "number" }} rules={{ required: false }} />
                            </Col>
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 4: الدعوات ==========
    const InvitationsTab = () => (
        <Collapse
            defaultActiveKey={['invitationEnable', 'senderMessages', 'receiverMessages', 'invitationContent', 'invitationImages']}
            items={[
                {
                    key: 'invitationEnable',
                    label: t("invitation_activation"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col span={24}>
                                <FieldBuilder label={t("enable_invitation")} name="enableInvitation" input={{ type: "checkBox" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'senderMessages',
                    label: t("sender_messages"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`titleSender-${index}`}>
                                    <FieldBuilder
                                        name={`titleInvitationSender[${lang?.code}]`}
                                        label={`${t("title_invitation_sender")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`descSender-${index}`}>
                                    <FieldBuilder
                                        name={`descriptionInvitationSender[${lang?.code}]`}
                                        label={`${t("description_invitation_sender")} (${lang?.name})`}
                                        input={{ type: "text-area" }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'receiverMessages',
                    label: t("receiver_messages"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`titleReceiver-${index}`}>
                                    <FieldBuilder
                                        name={`titleInvitationReceiver[${lang?.code}]`}
                                        label={`${t("title_invitation_receiver")} (${lang?.name})`}
                                        input={{ type: "text" }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`descReceiver-${index}`}>
                                    <FieldBuilder
                                        name={`descriptionInvitationReceiver[${lang?.code}]`}
                                        label={`${t("description_invitation_receiver")} (${lang?.name})`}
                                        input={{ type: "text-area" }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'invitationContent',
                    label: t("invitation_content"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`msgShare-${index}`}>
                                    <FieldBuilder
                                        name={`messageForSharingInvitation[${lang?.code}]`}
                                        label={`${t("message_for_sharing_invitation")} (${lang?.name})`}
                                        input={{ type: "text-area" }}
                                    />
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col span={24} key={`smallMsg-${index}`}>
                                    <FormItem label={`${t("small_message_for_invitation")} (${lang?.name})`}>
                                        <Controller
                                            name={`smallMessageForInvitation[${lang?.code}]`}
                                            rules={{ required: { value: false, message: t("field_is_required_message") } }}
                                            render={({ field: { ref, ...field } }) => (
                                                <RichEditor value={field?.value ?? ""} onChange={field?.onChange} />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col span={24} key={`bigMsg-${index}`}>
                                    <FormItem label={`${t("big_message_for_invitation")} (${lang?.name})`}>
                                        <Controller
                                            name={`bigMessageForInvitation[${lang?.code}]`}
                                            rules={{ required: { value: false, message: t("field_is_required_message") } }}
                                            render={({ field: { ref, ...field } }) => (
                                                <RichEditor value={field?.value ?? ""} onChange={field?.onChange} />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                            ))}
                            {languages?.map((lang, index) => (
                                <Col xs={24} sm={24} md={12} lg={12} key={`verySmallMsg-${index}`}>
                                    <FormItem label={`${t("very_small_message_for_invitation")} (${lang?.name})`}>
                                        <Controller
                                            name={`verySmallMessageForInvitation[${lang?.code}]`}
                                            rules={{ required: { value: false, message: t("field_is_required_message") } }}
                                            render={({ field: { ref, ...field } }) => (
                                                <RichEditor value={field?.value ?? ""} onChange={field?.onChange} />
                                            )}
                                        />
                                    </FormItem>
                                </Col>
                            ))}
                        </Row>
                    ),
                },
                {
                    key: 'invitationImages',
                    label: t("invitation_images"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {invitationImageFields.map((field) => (
                                <ImageFieldBlock
                                    key={field}
                                    fieldName={field}
                                    currentId={list?.[field]?.id}
                                    currentImage={list?.[field]}
                                    newImageUrl={newImages[field]}
                                    isChanged={imageChanged[field]}
                                    onUpload={() => handleUploadNewImage(field)}
                                    onImageChange={handleImageChange(field)}
                                    onRemoveNewImage={(restoreCurrentImage) => handleRemoveNewImage(field, restoreCurrentImage)}
                                    setWithoutBackgrounds={(value) => setWithoutBackgrounds((prev) => ({ ...prev, [field]: value }))}
                                    preserveOriginal={preserveOriginals[field]}
                                    setPreserveOriginal={(value) => setPreserveOriginals((prev) => ({ ...prev, [field]: value }))}
                                    onDeleteImage={() => handleImageDelete(field)}
                                    isImageLoading={imageLoading[field]}
                                    value={withoutBackgrounds[field]}
                                />
                            ))}
                        </Row>
                    ),
                },
            ]}
        />
    );

    // ========== Tab 5: المستثمرين والصور ==========
    const InvestorsMediaTab = () => (
        <Collapse
            defaultActiveKey={['investorVisibility', 'headerImages']}
            items={[
                {
                    key: 'investorVisibility',
                    label: t("investor_visibility"),
                    children: (
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("makeInvestorsNoResponseOffVisibility")} name="makeInvestorsNoResponseOffVisibility" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("ordersSummeryInvestorVisibility")} name="ordersSummeryInvestorVisibility" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={8}>
                                <FieldBuilder label={t("ordersSummeryGuestInvestorVisibility")} name="ordersSummeryGuestInvestorVisibility" input={{ type: "checkBox" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="startDateTimeOrdersSummeryInvestor" label={t("startDateTimeOrdersSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="endDateTimeOrdersSummeryInvestor" label={t("endDateTimeOrdersSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="startDateTimeOrdersGuestSummeryInvestor" label={t("startDateTimeOrdersGuestSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <FieldBuilder name="endDateTimeOrdersGuestSummeryInvestor" label={t("endDateTimeOrdersGuestSummeryInvestor")} input={{ type: "date-picker" }} />
                            </Col>
                        </Row>
                    ),
                },
                {
                    key: 'headerImages',
                    label: t("header_images"),
                    children: (
                        <Row gutter={[16, 8]}>
                            {headerImageFields.map((field) => (
                                <ImageFieldBlock
                                    key={field}
                                    fieldName={field}
                                    currentId={list?.[field]?.id}
                                    currentImage={list?.[field]}
                                    newImageUrl={newImages[field]}
                                    isChanged={imageChanged[field]}
                                    onUpload={() => handleUploadNewImage(field)}
                                    onImageChange={handleImageChange(field)}
                                    onRemoveNewImage={(restoreCurrentImage) => handleRemoveNewImage(field, restoreCurrentImage)}
                                    setWithoutBackgrounds={(value) => setWithoutBackgrounds((prev) => ({ ...prev, [field]: value }))}
                                    preserveOriginal={preserveOriginals[field]}
                                    setPreserveOriginal={(value) => setPreserveOriginals((prev) => ({ ...prev, [field]: value }))}
                                    onDeleteImage={() => handleImageDelete(field)}
                                    isImageLoading={imageLoading[field]}
                                    value={withoutBackgrounds[field]}
                                />
                            ))}
                        </Row>
                    ),
                },
            ]}
        />
    );

    const tabItems = [
        {
            key: 'pricing',
            label: <span><DollarOutlined /> {t("pricing_and_payment")}</span>,
            children: <PricingPaymentTab />,
        },
        {
            key: 'delivery',
            label: <span><CarOutlined /> {t("delivery_and_orders")}</span>,
            children: <DeliveryOrdersTab />,
        },
        {
            key: 'app',
            label: <span><MobileOutlined /> {t("app_and_communication")}</span>,
            children: <AppCommunicationTab />,
        },
        {
            key: 'invitations',
            label: <span><GiftOutlined /> {t("invitations")}</span>,
            children: <InvitationsTab />,
        },
        {
            key: 'investors',
            label: <span><TeamOutlined /> {t("investors_and_media")}</span>,
            children: <InvestorsMediaTab />,
        },
    ];

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.contentWrapper}>
                    <span className={styles.title}>
                        {t("update_general_settings")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("list") ? (
                        <div className={styles.loadingWrapper}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Tabs
                                defaultActiveKey="pricing"
                                items={tabItems}
                                size="middle"
                                tabBarStyle={{ marginBottom: 8 }}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.stickyFooter}>
                    <Button
                        onClick={() => {
                            reset();
                            navigate(-1);
                        }}
                    >
                        {t("cancel")}
                    </Button>
                    <Button loading={loadingSubmit} htmlType="submit" type="primary">
                        {t("save")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

const GeneralSettingForm = () => {
    return (
        <GeneralSettingContextProvider>
            <Form />
        </GeneralSettingContextProvider>
    );
};

export default GeneralSettingForm;
